"use client";
import React, { useState } from "react";

export default function BookingSection() {
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      {/* Final CTA Section */}
      <section className="bg-pine py-20 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <span className="text-xs font-medium tracking-[0.3em] uppercase text-linen/50 mb-4 block">
            Start Today
          </span>
          <h2 className="font-serif text-display-sm text-linen font-light leading-tight mb-6">
            The best time to invest<br />
            <em className="italic">in Kashmir was yesterday.</em>
          </h2>
          <p className="text-base text-linen/70 font-light leading-relaxed mb-10 max-w-lg mx-auto">
            The second best time is now. Land prices are rising. Supply is limited. Every month you wait is a month of appreciation you miss. Book a free consultation today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#contact"
              className="bg-amber text-linen text-sm font-medium px-8 py-4 rounded-full hover:bg-amber-hover transition-colors duration-300 whitespace-nowrap">
              Book Free Consultation
            </a>
            <a
              href="https://wa.link/a6acna"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-linen/30 text-linen text-sm font-medium px-8 py-4 rounded-full hover:bg-linen/10 transition-colors duration-300 whitespace-nowrap">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-linen py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Contact Info */}
            <div>
              <span className="text-xs font-medium tracking-[0.3em] uppercase text-pine-light mb-4 block">
                Get in Touch
              </span>
              <h2 className="font-serif text-display-sm text-pine font-light leading-tight mb-6">
                Let&apos;s talk about<br />
                <em className="italic">your investment.</em>
              </h2>
              <p className="text-base text-pine-light font-light leading-relaxed mb-10">
                Whether you have a specific plot in mind or are just starting to explore — we are here to help. No pressure. No sales pitch. Just honest advice.
              </p>

              <div className="space-y-5">
                {[
                  {
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    ),
                    label: "Phone",
                    value: "+91 6006718476",
                    href: "tel:+916006718476",
                  },
                  {
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    ),
                    label: "WhatsApp",
                    value: "+91 6006718476",
                    href: "https://wa.link/a6acna",
                  },
                  {
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    ),
                    label: "Email",
                    value: "masif77407@gmail.com",
                    href: "mailto:masif77407@gmail.com",
                  },
                  {
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    ),
                    label: "Location",
                    value: "Srinagar, Kashmir, India",
                    href: "#",
                  },
                ].map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="flex items-center gap-4 group">
                    <div className="w-10 h-10 rounded-xl bg-amber/10 flex items-center justify-center text-amber flex-shrink-0 group-hover:bg-amber group-hover:text-linen transition-colors duration-200">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-xs text-pine-light font-medium tracking-wide uppercase">{item.label}</p>
                      <p className="text-sm text-pine font-medium">{item.value}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Right: Enquiry Form */}
            <div className="bg-fog/20 rounded-3xl p-8 border border-fog/40">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-amber/10 flex items-center justify-center mx-auto mb-6">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C48B4A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <h3 className="font-serif text-3xl font-light text-pine mb-3">We&apos;ll be in touch.</h3>
                  <p className="text-pine-light font-light text-base max-w-sm mx-auto">
                    Thank you for your enquiry. Our team will contact you within 24 hours.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl font-light text-pine mb-2">Send an Enquiry</h3>
                  <p className="text-sm text-pine-light font-light mb-6">We respond within 24 hours.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-pine-light tracking-wide uppercase block mb-1.5">Full Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        required
                        className="w-full bg-white border border-fog/60 rounded-xl px-4 py-3 text-sm text-pine placeholder:text-pine-light/50 focus:outline-none focus:border-amber transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-pine-light tracking-wide uppercase block mb-1.5">Phone / WhatsApp</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX"
                        required
                        className="w-full bg-white border border-fog/60 rounded-xl px-4 py-3 text-sm text-pine placeholder:text-pine-light/50 focus:outline-none focus:border-amber transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-pine-light tracking-wide uppercase block mb-1.5">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        className="w-full bg-white border border-fog/60 rounded-xl px-4 py-3 text-sm text-pine placeholder:text-pine-light/50 focus:outline-none focus:border-amber transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-pine-light tracking-wide uppercase block mb-1.5">Message (Optional)</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us your budget, preferred location, or any questions..."
                        rows={3}
                        className="w-full bg-white border border-fog/60 rounded-xl px-4 py-3 text-sm text-pine placeholder:text-pine-light/50 focus:outline-none focus:border-amber transition-colors resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-amber text-linen font-medium py-3.5 rounded-xl hover:bg-amber-hover transition-colors">
                      Send Enquiry
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}